import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import LoadingButton from "components/LoadingButton";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import { changePasswordWithPasswordId, loginWithOtp } from "services/auth.service";
import { setUserData, setJwtTokens } from "../../storage";
import { postAuthTokenToNative } from "utils/nativeBridge";
import { useLoginPage } from "hooks/useLoginPage";
import { useAppNavigate } from "hooks/useAppNavigate";
import { isValidPassword } from "utils/validators";
import PasswordInput from "components/PasswordInput";
import { useText } from "context/TextProvider";

function ForceChangePassword(): JSX.Element {
  const text = useText();
  const { changeId } = useParams<{ changeId: string }>();
  const appNavigate = useAppNavigate();
  const { configId, loginPageConfig } = useLoginPage();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!newPassword) {
      setError(text.changePassword.newPasswordRequired);
      return;
    }
    if (!isValidPassword(newPassword)) {
      setError(text.changePassword.passwordRequirements);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(text.changePassword.passwordsDoNotMatch);
      return;
    }
    setLoading(true);
    let oneTimePassword: string;
    try {
      ({ oneTimePassword } = await changePasswordWithPasswordId({
        passwordId: changeId ?? "",
        password: newPassword,
        url: loginPageConfig?.urls?.submit_forgot_password_otp,
      }));
    } catch {
      setError(text.changePassword.resetFailed);
      setLoading(false);
      return;
    }
    try {
      const authData = await loginWithOtp({
        oneTimePassword,
        url: loginPageConfig?.urls?.otp_login,
      });
      setUserData(authData);
      const { user, ...tokens } = authData;
      setJwtTokens(tokens);
      postAuthTokenToNative({
        token: authData.token,
        refreshToken: authData.refreshToken,
        tokenExpirationInstant: authData.tokenExpirationInstant,
        refreshUrl: loginPageConfig?.urls?.refresh_url,
      });
      appNavigate(`/?config_id=${configId}`);
    } catch {
      setError(text.changePassword.passwordChangedLoginFailed ?? text.changePassword.resetFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta name="Force Change Password" />
      <PageTitle>{text.changePassword.title}</PageTitle>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <form onSubmit={handleChangePassword}>
        <PasswordInput
          id="newPassword"
          label={text.changePassword.newPasswordLabel}
          value={newPassword}
          placeholder={text.changePassword.newPasswordPlaceholder}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
        />
        {newPassword && !isValidPassword(newPassword) && <ErrorMessage>{text.changePassword.passwordRequirements}</ErrorMessage>}
        <PasswordInput
          id="confirmPassword"
          label={text.changePassword.confirmPasswordLabel}
          value={confirmPassword}
          placeholder={text.changePassword.confirmPasswordPlaceholder}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />

        <LoadingButton
          loading={loading}
          type="submit"
          disabled={loading}
          sx={{ width: "100%", height: "3.5rem", mb: "1rem" }}
        >
          {text.changePassword.updatePasswordButton}
        </LoadingButton>
      </form>

      <Box sx={{ textAlign: "center", fontSize: "0.875rem", color: "text.secondary", mb: "1.5rem" }}>
        <MuiLink color="secondary" component={RouterLink} to={`/login?config_id=${configId}`} replace sx={{ fontWeight: 500 }}>
          {text.changePassword.backToLoginLink}
        </MuiLink>
      </Box>
    </>
  );
}

export default ForceChangePassword;
